import NiceModal from '@ebay/nice-modal-react';
import { PageViewEventLogger, useUserEventLogger } from '@repo/tracking-service';
import { Alert, Divider, List, Switch, useToast } from '@repo/ui';
import { TermDrawer } from '@repo/user-sign';
import { Icon } from '@zenterprise-inc/ui';
import { format } from 'date-fns';
import { ReactElement, useState } from 'react';
import { useLazyLoadQuery, useMutation } from 'react-relay';

import { marketingTermQuery } from '@/__generated__/marketingTermQuery.graphql';
import { marketingTermUpdateMutation } from '@/__generated__/marketingTermUpdateMutation.graphql';
import s from '@/components/my/common/layout.module.scss';
import { MyNotificationPageTopNavigation } from '@/components/my/notification';
import { marketingTermAgreement, updateMarketingTerm } from '@/graphql/query/my/marketing-term';
import { NOTIFICATION_TERMS } from '@/lib/constants/terms';
import { NotificationTermKey, NotificationTermState } from '@/lib/types/auth';

export default function MyNotificationPage() {
  const defaultTermState = {
    all: false,
    marketing: false,
    advertising: false
  };
  const data = useLazyLoadQuery<marketingTermQuery>(
    marketingTermAgreement,
    {},
    {
      fetchPolicy: 'network-only'
    }
  );
  const [termState, setTermState] = useState<NotificationTermState>(() => {
    const initialState =
      data.getRefundUserMarketingTerm.terms?.reduce((acc: Record<NotificationTermKey, boolean>, term) => {
        const stateKey = term.termKey.replace('bznav-taxrefund-', '') as NotificationTermKey;
        acc[stateKey] = term.isApproved;
        return acc;
      }, defaultTermState) ?? defaultTermState;

    initialState.all = initialState.marketing && initialState.advertising;
    return initialState;
  });
  const { toast } = useToast();
  const { sendClickEvent } = useUserEventLogger();
  const [commit] = useMutation<marketingTermUpdateMutation>(updateMarketingTerm);

  const handleOpenTermDrawer = () => {
    sendClickEvent('benefit-noti_body_marketing-terms');
    NiceModal.show(TermDrawer, { link: 'https://bznav.com/popup/terms/bznav-taxrefund-marketing/', keepMounted: true });
  };

  const ToggleAgreeState = (key: NotificationTermKey) => (checked: boolean) => {
    sendClickEvent('benefit-noti_body_on-off', { term: key, status: checked ? 'on' : 'off' });
    let nextState: NotificationTermState = { ...termState, [key]: checked };
    if (key === 'all') {
      nextState = { all: checked, marketing: checked, advertising: checked };
    } else {
      nextState.all = nextState.marketing && nextState.advertising;
    }
    setTermState(nextState);

    NOTIFICATION_TERMS.forEach((term) => {
      if (term.termKey && termState[term.key] !== nextState[term.key]) {
        commit({
          variables: {
            isApproved: nextState[term.key],
            termKey: term.termKey
          }
        });
      }
    });

    let msg = `개인정보 수집·이용(마케팅) 및 광고성 정보수신${checked ? '에 동의했어요.' : ' 동의를 철회했어요.'}`;
    if (key === 'marketing') {
      msg = `개인정보 수집·이용 동의(마케팅)${checked ? ' 했어요.' : '를 철회했어요.'}`;
    } else if (key === 'advertising') {
      msg = `광고성 정보수신 동의${checked ? '했어요.' : '를 철회했어요.'}`;
    }

    toast({
      title: `${format(new Date(), 'yy년 M월 d일')}, ${msg}`,
      variant: 'enabled'
    });
  };

  return (
    <>
      <MyNotificationPageTopNavigation />
      <main className={s.main_content}>
        <section>
          <article className={'flex flex-col pt-normal-small pb-normal-medium px-4.5'}>
            {NOTIFICATION_TERMS.map((term) => (
              <div key={term.key}>
                <List
                  title={term.title}
                  description={term.description}
                  size={term.size}
                  secondaryAction={
                    <div className={'flex items-center'}>
                      <Switch checked={termState[term.key]} onCheckedChange={ToggleAgreeState(term.key)} />
                    </div>
                  }
                />
                {term.key === 'all' && <Divider className={'my-normal-small'} />}
              </div>
            ))}
            {!termState.all && (termState.marketing || termState.advertising) && (
              <Alert variant={'warning'} size={'small'} className={'mt-normal-small mb-tiny-medium'}>
                혜택·이벤트 정보를 받으려면 위의 2가지 항목을 모두 동의해야해요.
              </Alert>
            )}
          </article>
        </section>
        <Divider size={'large'} />
        <section>
          <article className={'flex flex-col pt-normal-small pb-normal-medium px-4.5'}>
            <List
              title={'개인정보 수집·이용 동의(마케팅)'}
              size={'small'}
              onClick={handleOpenTermDrawer}
              primaryAction={true}
              supportingElement={
                <div className={'flex items-center text-neutral-foreground-lowest body-small-medium'}>
                  <span className={'text-neutral-foreground-low'}>{termState.marketing ? '동의함' : '철회함'}</span>
                  <Icon type={'arrow-right'} size={18} className={'ml-tiny-medium'} />
                </div>
              }
            />
          </article>
        </section>
      </main>
    </>
  );
}

MyNotificationPage.getLayout = function getLayout(page: ReactElement) {
  return <PageViewEventLogger pageName={'benefit-noti'}>{page}</PageViewEventLogger>;
};
